import type { FormEvent } from 'react';
import { Address, ParticipantDetails, ParticipantType } from '@regigov/contracts';
import { Field } from './ui';

export type ParticipantFormValues = {
  legalName: string;
  tradingName: string;
  abn: string;
  participantType: ParticipantDetails['participantType'];
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  line1: string;
  line2: string;
  suburb: string;
  state: Address['state'];
  postcode: string;
};

export function toFormValues(d?: Partial<ParticipantDetails> | null): ParticipantFormValues {
  return {
    legalName: d?.legalName ?? '',
    tradingName: d?.tradingName ?? '',
    abn: d?.abn ?? '',
    participantType: d?.participantType ?? 'installer',
    contactName: d?.contactName ?? '',
    contactEmail: d?.contactEmail ?? '',
    contactPhone: d?.contactPhone ?? '',
    line1: d?.address?.line1 ?? '',
    line2: d?.address?.line2 ?? '',
    suburb: d?.address?.suburb ?? '',
    state: d?.address?.state ?? 'NSW',
    postcode: d?.address?.postcode ?? '',
  };
}

export function toDetails(v: ParticipantFormValues): unknown {
  return {
    legalName: v.legalName,
    tradingName: v.tradingName || undefined,
    abn: v.abn.replace(/\s/g, ''),
    participantType: v.participantType,
    contactName: v.contactName,
    contactEmail: v.contactEmail,
    contactPhone: v.contactPhone,
    address: { line1: v.line1, line2: v.line2 || undefined, suburb: v.suburb, state: v.state, postcode: v.postcode },
  };
}

export type FieldErrors = Partial<Record<string, string>>;

export function validateDetails(v: ParticipantFormValues): { details?: ParticipantDetails; errors: FieldErrors } {
  const parsed = ParticipantDetails.safeParse(toDetails(v));
  if (parsed.success) return { details: parsed.data, errors: {} };
  const errors: FieldErrors = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path.at(-1);
    if (typeof key === 'string' && !errors[key]) errors[key] = issue.message;
  }
  return { errors };
}

const STATES = Address.shape.state.options;
const TYPES = ParticipantType.options;

interface Props {
  values: ParticipantFormValues;
  onChange: (values: ParticipantFormValues) => void;
  errors?: FieldErrors;
  /** When false, identity fields (legal name, ABN, type) are read-only. */
  identityEditable?: boolean;
  disabled?: boolean;
}

export function ParticipantFields({ values, onChange, errors = {}, identityEditable = true, disabled }: Props) {
  const set = (k: keyof ParticipantFormValues) => (e: FormEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange({ ...values, [k]: e.currentTarget.value });
  const err = (k: string) => errors[k] && <span className="error hint">{errors[k]}</span>;
  const ro = !identityEditable || disabled;

  return (
    <>
      <fieldset>
        <legend>Organisation</legend>
        <div className="grid2">
          <Field label="Legal name">
            <input name="legalName" value={values.legalName} onChange={set('legalName')} readOnly={ro} required />
            {err('legalName')}
          </Field>
          <Field label="Trading name (optional)">
            <input name="tradingName" value={values.tradingName} onChange={set('tradingName')} disabled={disabled} />
            {err('tradingName')}
          </Field>
          <Field label="ABN" hint="11 digits">
            <input name="abn" value={values.abn} onChange={set('abn')} readOnly={ro} inputMode="numeric" required />
            {err('abn')}
          </Field>
          <Field label="Participant type">
            <select name="participantType" value={values.participantType} onChange={set('participantType')} disabled={ro}>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {err('participantType')}
          </Field>
        </div>
      </fieldset>

      <fieldset>
        <legend>Primary contact</legend>
        <div className="grid2">
          <Field label="Contact name">
            <input name="contactName" value={values.contactName} onChange={set('contactName')} disabled={disabled} required />
            {err('contactName')}
          </Field>
          <Field label="Email">
            <input name="contactEmail" type="email" value={values.contactEmail} onChange={set('contactEmail')} disabled={disabled} required />
            {err('contactEmail')}
          </Field>
          <Field label="Phone">
            <input name="contactPhone" value={values.contactPhone} onChange={set('contactPhone')} disabled={disabled} required />
            {err('contactPhone')}
          </Field>
        </div>
      </fieldset>

      <fieldset>
        <legend>Registered address</legend>
        <div className="grid2">
          <Field label="Address line 1">
            <input name="line1" value={values.line1} onChange={set('line1')} disabled={disabled} required />
            {err('line1')}
          </Field>
          <Field label="Address line 2 (optional)">
            <input name="line2" value={values.line2} onChange={set('line2')} disabled={disabled} />
          </Field>
          <Field label="Suburb">
            <input name="suburb" value={values.suburb} onChange={set('suburb')} disabled={disabled} required />
            {err('suburb')}
          </Field>
          <Field label="State">
            <select name="state" value={values.state} onChange={set('state')} disabled={disabled}>
              {STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Postcode">
            <input name="postcode" value={values.postcode} onChange={set('postcode')} disabled={disabled} inputMode="numeric" required />
            {err('postcode')}
          </Field>
        </div>
      </fieldset>
    </>
  );
}
